// GifPicker — Teams-style searchable GIF picker for the comment composer.
//
// A trigger button opens a popover: a debounced search box, trending GIFs on
// first open, and a thumbnail grid. Selecting a thumbnail hands the chosen
// GifResult back to the caller (staged into the comment draft) and closes the
// popover. Backed by GET /api/v1/gifs/search (server-side Giphy proxy).

import { useCallback, useEffect, useRef, useState } from "react";
import { searchGifs, type GifResult } from "./gifs-api";

const SEARCH_DEBOUNCE_MS = 300;

export interface GifPickerProps {
  /** Called with the chosen GIF; the caller stages it and closes this picker. */
  onSelect: (gif: GifResult) => void;
}

export function GifPicker({ onSelect }: GifPickerProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<GifResult[]>([]);
  const [enabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<number | undefined>(undefined);

  const runSearch = useCallback(async (q: string) => {
    setLoading(true);
    setError(null);
    try {
      const resp = await searchGifs(q);
      setResults(resp.results);
      setEnabled(resp.enabled);
    } catch {
      setError("Could not load GIFs — try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  // Load trending as soon as the popover opens.
  useEffect(() => {
    if (open) void runSearch("");
    // Only on open — subsequent queries are driven by the debounced effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Debounced search-as-you-type.
  useEffect(() => {
    if (!open) return;
    window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      void runSearch(query);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    function handlePointerDown(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  const handlePick = useCallback(
    (gif: GifResult) => {
      onSelect(gif);
      setOpen(false);
      setQuery("");
    },
    [onSelect],
  );

  return (
    <div className="gif-picker" data-testid="gif-picker" ref={wrapRef}>
      <button
        type="button"
        className="pd-actbtn"
        onClick={() => setOpen((v) => !v)}
        aria-label="Attach a GIF"
        aria-expanded={open}
        data-testid="gif-picker-trigger"
      >
        GIF
      </button>

      {open && (
        <div className="gif-popover" data-testid="gif-popover">
          <input
            type="text"
            className="gif-search-input"
            placeholder="Search GIFs…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search GIFs"
            autoFocus
            data-testid="gif-search-input"
          />

          {!enabled && (
            <p className="gif-empty-state" data-testid="gif-disabled-state">
              GIF search isn't set up yet — ask an admin to add a Giphy API key.
            </p>
          )}
          {enabled && error && (
            <p className="gif-empty-state" role="alert">
              {error}
            </p>
          )}
          {enabled && !error && loading && (
            <p className="gif-empty-state" data-testid="gif-loading">
              Loading…
            </p>
          )}
          {enabled && !error && !loading && results.length === 0 && (
            <p className="gif-empty-state" data-testid="gif-no-results">
              No GIFs found.
            </p>
          )}

          {enabled && !error && results.length > 0 && (
            <div className="gif-grid" data-testid="gif-grid">
              {results.map((gif) => (
                <button
                  key={gif.id}
                  type="button"
                  className="gif-thumb"
                  onClick={() => handlePick(gif)}
                  aria-label={`Attach GIF: ${gif.title}`}
                  data-testid="gif-thumb"
                >
                  <img src={gif.preview_url} alt={gif.title} loading="lazy" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default GifPicker;
